// Base
import type { Blueprint } from '@builder/base/blueprints/Blueprint';
import type { AggregateBlueprint } from '@builder/base/blueprints/AggregateBlueprint';
import { instanceOfAggregateBlueprint } from '@builder/base/blueprints/AggregateBlueprint';
import type { ValidationErrors } from '@builder/base/types/ValidationErrors';
import type { ValidatableBlueprint } from '@builder/base/blueprints/ValidatableBlueprint';
import { setValidationErrors, validateBlueprints } from '@builder/base/blueprints/ValidatableBlueprint';
import { RootAbstract } from '@builder/base/blueprints/RootBlueprint';

// Blueprints
import type { BlueprintDefinition } from '@builder/form/blueprints/BlueprintDefinition';
import type { FormBlueprint } from '@builder/form/blueprints/FormBlueprint';

// Traits
import type { EntryFactory } from '@builder/form/traits/EntryFactory';
import type { HasTitle } from '@builder/form/traits/HasTitle';
import type { HasDescription } from '@builder/form/traits/HasDescription';

// Entries
import type { Entry } from '@builder/form/entries/Entry';
import { entry } from '@builder/form/entries/Entry';
import type { ValidEntry } from '@builder/form/entries/ValidEntry';
import { instanceOfValidEntry } from '@builder/form/entries/ValidEntry';
import { RootEntryAbstract } from '@builder/form/entries/RootEntry';

// Types
import type { ProcessCallback } from '@builder/form/types/ProcessCallback';
import { defaultCallback } from '@builder/form/types/ProcessCallback';

// Form
import type { FormBuilderContract } from '@builder/form';
import { Definition as TableDefinition } from '../../../forms/blueprints/table';

// --------------------------------------------------

export const Definition: BlueprintDefinition = {
    type: 'form',
    name: '[[[Formularz]]]',
    group: 'none'
};

export enum ProgressChoice
{
    Steps = 'Steps',
    Bar = 'Bar',
    Tabs = 'Tabs',
    None = 'None'
}

export enum TabsType
{
    Tabs = 'Tabs',
    Pills = 'Pills',
    Underline = 'Underline'
}

export interface FormContract extends FormBlueprint, AggregateBlueprint, HasTitle, HasDescription
{
    sendButtonText: Record<string, string>;
    showSendButton: boolean;
    showPageNumbers: boolean;
    showPageTitles: boolean;
    progressType: ProgressChoice;
    tabsType: TabsType;
    tabsJustified: boolean;
    currentPage: number;
    category: number;
    additionalFields: string[];
}

export class FormType extends RootAbstract implements EntryFactory<FormEntry>, FormContract, ValidatableBlueprint
{
    public kind: 'form' = 'form' as const;
    public id: string;
    public type: string;
    public name: string;
    public components: Blueprint[] = [];
    public componentNames: { name: string; type: string }[] = [];
    public title: Record<string, string>;
    public showTitle: boolean;
    public description: Record<string, string>;
    public sendButtonText: Record<string, string>;
    public showSendButton: boolean;
    public showPageNumbers: boolean;
    public showPageTitles: boolean;
    public progressType: ProgressChoice;
    public tabsType: TabsType;
    public tabsJustified: boolean;
    public currentPage: number;
    public category: number;
    public errors: ValidationErrors;
    public additionalFields: string[];

    public constructor(id: string, name: string)
    {
        super();

        this.id = id;
        this.name = name;
        this.type = Definition.type;
        this.components = [];
        this.componentNames = [];
        this.title = { 'pl-PL': 'Formularz' };
        this.showTitle = false;
        this.description = {};
        this.sendButtonText = {};
        this.showSendButton = false;
        this.showPageNumbers = false;
        this.showPageTitles = true;
        this.progressType = ProgressChoice.Steps;
        this.tabsType = TabsType.Tabs;
        this.tabsJustified = false;
        this.currentPage = 1;
        this.category = null;
        this.errors = {};
        this.additionalFields = [];
    }

    public createEntry(data: any): FormEntry
    {
        return new FormEntry(data);
    }

    public validate(): Record<string, ValidationErrors>
    {
        this.errors = {};

        const defaultLocale = useLocalization().$i18n.defaultLocale();
        const TITLE_LENGTH = 250;

        if (!this.title[defaultLocale])
            this.errors.title = ['[[[Tytuł formularza jest wymagany]]]'];
        else if (Object.values(this.title).some(value => value.length > TITLE_LENGTH))
            this.errors.title = [`[[[Tytuł formularza nie może być dłuższy niż %0 znaków|||${TITLE_LENGTH}]]]`];

        if (!this.category)
            this.errors.category = ['[[[Kategoria jest wymagana]]]'];

        const errors = {
            [this.name]: this.errors,
            ...validateBlueprints(this.components)
        };

        return errors;
    }

    public setErrors(errors: Record<string, string[]>): void
    {
        setValidationErrors(this, errors);
    }
}

export const instanceOfFormType = (object: any): object is FormType =>
{
    return instanceOfAggregateBlueprint(object) && 'type' in object && object.type === Definition.type;
};

export class FormEntry extends RootEntryAbstract
{
    [prop: string]: any;

    public override data: any;

    public constructor(data: any = null)
    {
        super(Definition.type);

        if (data !== null)
        {
            Object.entries(data).forEach(([name, value]) =>
            {
                this[name] = value;
            });
        }
    }

    public async collect(form: FormContract, builder: FormBuilderContract, preprocess: ProcessCallback = defaultCallback): Promise<Entry>
    {
        await preprocess(form, this, builder.blueprintId, builder.entryId);

        const result: any = {
            type: this.type
        };

        const blueprints = builder.schema.components().filter((p) =>
        {
            const parent = builder.schema.parent(p);

            return parent?.type !== TableDefinition.type;
        });

        for (let i = 0; i < blueprints.length; i++)
        {
            const name = blueprints[i].name;

            if (instanceOfValidEntry(this[name]))
            {
                const entry = this[name] as ValidEntry<any>;
                const blueprint = builder.schema.find(name);

                result[name] = await entry.collect(blueprint, builder, preprocess);
            }
        }

        return entry(result);
    }

    public validate(form: FormContract, builder: FormBuilderContract): boolean
    {
        this.errors = {};

        builder.schema.components().forEach((blueprint) =>
        {
            if (instanceOfValidEntry(this[blueprint.name]))
            {
                const entry = this[blueprint.name] as ValidEntry<Record<string, ValidEntry<any>>[]>;

                if (blueprint.type !== TableDefinition.type)
                {
                    if (!entry.validate(blueprint, builder))
                    {
                        Object.keys(entry.errors).forEach((key) =>
                        {
                            this.errors[`${blueprint.name}.${key}`] = entry.errors[key];
                        });
                    }
                }
                else
                {
                    // TODO: Do sprawdzenia. Po co iterujemy po komponentach jak już w pętli wyżej też to robimy?
                    // Powinniśmy pobrać tylko komponenty dla któych rodzicem jest tabelka.
                    // To raczej powinno wyglądać tak:

                    // const tableBlueprint = blueprint as TableType;

                    // tableBlueprint.columns.forEach((column) =>
                    // {
                    //     entry.data.forEach((row) =>
                    //     {
                    //         if (instanceOfValidEntry(row[column.columnHeader]))
                    //         {
                    //             const tableEntry = row[column.columnHeader] as ValidEntry<any>;

                    //             if (!tableEntry.validate(tableBlueprint, builder))
                    //             {
                    //                 Object.keys(tableEntry.errors).forEach((key) =>
                    //                 {
                    //                     this.errors[`${blueprint.name}.${column.columnHeader}.${key}`] = tableEntry.errors[key];
                    //                 });
                    //             }
                    //         }
                    //     });
                    // });

                    builder.schema.components().forEach((tableBlueprint) =>
                    {
                        entry.data.forEach((row) =>
                        {
                            if (instanceOfValidEntry(row[tableBlueprint.name]))
                            {
                                const tableEntry = row[tableBlueprint.name] as ValidEntry<any>;

                                if (!tableEntry.validate(tableBlueprint, builder))
                                {
                                    Object.keys(tableEntry.errors).forEach((key) =>
                                    {
                                        this.errors[`${blueprint.name}.${tableBlueprint.name}.${key}`] = tableEntry.errors[key];
                                    });
                                }
                            }
                        });
                    });
                }
            }
        });

        return this.valid();
    }

    public find(name: string): ValidEntry<any>
    {
        if (name in this && instanceOfValidEntry(this[name]))
        {
            return this[name];
        }

        return null;
    }

    public getFields(): Record<string, FormEntry>
    {
        const formFields: any = {};

        Object.entries(this).forEach(([key, entry]) =>
        {
            if (instanceOfValidEntry(entry))
            {
                formFields[key] = entry;
            }
        });

        return formFields;
    }
}
